// Typed rejection → the words a member reads (bead hf-7o6).
//
// Same job and same reasoning as features/claim/claimCopy.ts: contracts stays
// copy-free and product-language stays schema-free, so the mapping lives in the app
// where both are already dependencies.
//
// TOTAL ON PURPOSE. A new member of `CommonRejection` is a compile error here rather
// than a blank panel on the one surface in the product where a person is handling a
// credential. There is no `default` arm, and there must never be one.

import type { CommonRejection } from "@herefor/contracts";
import { ERROR_MESSAGES, type ActionableMessage } from "@herefor/product-language";

/**
 * Coarse liveness, in the member's units rather than milliseconds.
 *
 * The stamp behind it only moves once an hour (LIVENESS_RESOLUTION_MS in
 * convex/http/icsFeed.ts), so anything finer than this would be precision the data
 * does not have. Hours up to a day, then days.
 */
export type FeedFetchAge =
  | { readonly unit: "never" }
  | { readonly unit: "withinHour" }
  | { readonly unit: "hours"; readonly value: number }
  | { readonly unit: "days"; readonly value: number };

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * How long ago the feed was last fetched.
 *
 * Pure, and takes `now` as an argument so the rendered sentence is deterministic in a
 * test rather than a function of when the suite happened to run.
 *
 * A stamp in the future — clock skew between the member's device and the deployment —
 * reads as "within the hour" rather than as a negative number of hours. It is the
 * honest answer to "recently?" and it cannot render an absurdity.
 */
export function fetchAge(lastServedAt: number | undefined, now: number): FeedFetchAge {
  if (lastServedAt === undefined) return { unit: "never" };

  const elapsed = now - lastServedAt;
  if (elapsed < HOUR_MS) return { unit: "withinHour" };
  if (elapsed < DAY_MS) return { unit: "hours", value: Math.floor(elapsed / HOUR_MS) };

  return { unit: "days", value: Math.floor(elapsed / DAY_MS) };
}

/**
 * The message for any rejection this surface can receive.
 *
 * The fallbacks are chosen to be TRUE rather than generic, for the same reason as the
 * claim surface: telling somebody their press failed when in fact they were never
 * allowed to do it sends them back to press it again.
 */
export function messageFor(rejection: CommonRejection): ActionableMessage {
  switch (rejection.kind) {
    case "missing_capability":
      return ERROR_MESSAGES.permissionDenied;
    case "not_a_member":
      return ERROR_MESSAGES.accessRemoved;
    case "circle_not_found":
      return ERROR_MESSAGES.itemUnavailable;
    // `setFeedTitleMode` answers `not_found` when there is no feed to change — which
    // a member reaches by turning the link off in another tab. "This item is no longer
    // available" is exactly what happened.
    case "not_found":
      return ERROR_MESSAGES.itemUnavailable;
    // Reached by the transport-failure path in useCalendarFeed as well as by a real
    // validation refusal. Both mean the same thing to the member: nothing was saved.
    case "validation_failed":
      return ERROR_MESSAGES.serviceUnavailable;
    // No call from this surface binds a revision, so this cannot arrive today. It is
    // answered rather than left to a default that would swallow a genuine bug.
    case "revision_stale":
      return ERROR_MESSAGES.serviceUnavailable;
  }
}
